import { useContext, useEffect, useRef } from 'react';
import type { ViewDefinition } from '@cognite/sdk';
import type { ViewMappingReference } from '../../features/app-config/cdm-types';

import { UnleashKeys } from '../../constants/unleash-keys';

import {
  assertCdmOfflineConfiguration,
  getCdmConfigurationQueryKey,
} from './cdm-offline-configuration';
import { UseSaveCdmOfflineConfigurationContext } from './cdm-use-save-offline-configuration.context';
import { getSavedCdmConfigurationQueryKey } from './cdm-use-saved-configuration';
import { offlineStores } from './use-offline-store/consts';

/**
 * Saves the configuration CDM startup has loaded, once groups, views and the offline store are ready.
 * References must be memoized; undefined views suspend saving until the views have loaded.
 */
export const useSaveCdmOfflineConfiguration = ({
  assetViewReferences,
  assetViews,
}: {
  assetViewReferences: ViewMappingReference[];
  assetViews: ViewDefinition[] | undefined;
}): void => {
  const {
    useQueryClient,
    captureException,
    useOfflineStore,
    useCurrentUserContext,
    useUserGroupsQuery,
    useCdmSelectedLocationContext,
    useFlag,
  } = useContext(UseSaveCdmOfflineConfigurationContext);
  const queryClient = useQueryClient();
  const { offlineStore, scope } = useOfflineStore(offlineStores.Configuration);
  const { user } = useCurrentUserContext();
  const groups = useUserGroupsQuery();
  const { selectedLocationConfig } = useCdmSelectedLocationContext();
  const isSavingEnabled = useFlag(UnleashKeys.offline_multiple_checklists);
  const queue = useRef<Promise<void>>(Promise.resolve());

  // Within one mounted hook, replacements run one at a time so an older write cannot finish last.
  useEffect(() => {
    if (
      !isSavingEnabled ||
      !groups.isSuccess ||
      !assetViews ||
      !offlineStore ||
      !scope
    ) {
      return undefined;
    }
    let current = true;
    queue.current = queue.current.then(async () => {
      if (!current) return;
      try {
        const configuration = assertCdmOfflineConfiguration(
          {
            user: {
              externalId: user.externalId,
              preferences: user.preferences,
            },
            groups: (groups.data ?? []).map((group) => group.name),
            locations: selectedLocationConfig ? [selectedLocationConfig] : [],
            assetViewReferences,
            assetViews,
          },
          scope
        );
        await offlineStore.save(
          getCdmConfigurationQueryKey(scope),
          configuration
        );
        queryClient.setQueriesData(
          { queryKey: getSavedCdmConfigurationQueryKey(scope), exact: true },
          configuration
        );
      } catch (error) {
        captureException(error, {
          level: 'warning',
          tags: { offlineConfiguration: 'save' },
        });
      }
    });
    return () => {
      current = false;
    };
  }, [
    queryClient,
    isSavingEnabled,
    user,
    groups.data,
    groups.isSuccess,
    selectedLocationConfig,
    assetViewReferences,
    assetViews,
    offlineStore,
    scope,
  ]);
};
